import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /** @type {number} */
  #dragStartX = 0;

  /** @type {boolean} */
  #dragging = false;

  /** @type {boolean} */
  #didDrag = false;

  /** @type {number|null} */
  #activePointerId = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerUp);
    this.addEventListener('click', this.#onClickCapture, true);

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerUp);
    this.removeEventListener('click', this.#onClickCapture, true);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  get #slideCount() {
    return (this.refs.slides ?? []).length;
  }

  /**
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    if (this.#slideCount < 2) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest('a, button, .slideshow-control')) return;

    this.#dragging = true;
    this.#didDrag = false;
    this.#dragStartX = event.clientX;
    this.#activePointerId = event.pointerId;
    this.suspend();

    try {
      this.setPointerCapture(event.pointerId);
    } catch {
      // Ignore if capture is unavailable.
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (!this.#dragging || event.pointerId !== this.#activePointerId) return;
    if (Math.abs(event.clientX - this.#dragStartX) < 8) return;

    this.#didDrag = true;
    this.setAttribute('dragging', '');
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerUp = (event) => {
    if (!this.#dragging || (this.#activePointerId != null && event.pointerId !== this.#activePointerId)) return;

    const dx = event.clientX - this.#dragStartX;
    this.#dragging = false;
    this.#activePointerId = null;
    this.removeAttribute('dragging');

    try {
      this.releasePointerCapture(event.pointerId);
    } catch {
      // Ignore if capture was never set.
    }

    if (Math.abs(dx) >= 40) {
      this.#didDrag = true;
      if (dx < 0) this.next();
      else this.previous();
    }

    this.resume();
  };

  /**
   * Suppress link clicks after a drag gesture.
   * @param {MouseEvent} event
   */
  #onClickCapture = (event) => {
    if (!this.#didDrag) return;
    this.#didDrag = false;
    event.preventDefault();
    event.stopPropagation();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
